import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  ArrowLeft, Video, Image, Wand2, Clock, Download, Sparkles, 
  RefreshCw, Upload, X, Play, ChevronDown, Settings, Trash2,
  Film, Layers, Zap, Monitor, Check
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Textarea } from '../components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Model configurations — keys map to Kling API model_name + mode
const MODELS = {
  'kling-v2-master|pro': { name: 'Kling 2.0 Master', badge: 'Recommended', model_name: 'kling-v2-master', mode: 'pro', durations: [5, 10], resolutions: ['1080p'] },
  'kling-v1-6|pro':     { name: 'Kling 1.6 Pro',    badge: 'High Quality', model_name: 'kling-v1-6',    mode: 'pro', durations: [5, 10], resolutions: ['1080p'] },
  'kling-v1-6|std':     { name: 'Kling 1.6 Std',    badge: 'Fast',         model_name: 'kling-v1-6',    mode: 'std', durations: [5, 10], resolutions: ['720p']  },
};

const ASPECT_RATIOS = [
  { value: '16:9', label: '16:9', desc: 'Landscape', icon: '▭' },
  { value: '9:16', label: '9:16', desc: 'Portrait', icon: '▯' },
  { value: '1:1', label: '1:1', desc: 'Square', icon: '□' },
];

const KlingToolPage = () => {
  const { platformId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [platform, setPlatform] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generations, setGenerations] = useState([]);
  
  // Generation mode
  const [mode, setMode] = useState('text2video'); // 'text2video' | 'image2video'
  
  // Form state
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [model, setModel] = useState('kling-v2-master|pro');
  const [duration, setDuration] = useState(5);
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [cfgScale, setCfgScale] = useState(0.5);
  
  // Image upload state
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  
  // UI state
  const [showSettings, setShowSettings] = useState(false);
  const [selectedGeneration, setSelectedGeneration] = useState(null);

  useEffect(() => {
    loadData();
  }, [platformId]);

  const loadData = async () => {
    try {
      const [platformsRes, generationsRes] = await Promise.all([
        axios.get(`${API_URL}/api/platforms`, { withCredentials: true }),
        axios.get(`${API_URL}/api/generations`, { withCredentials: true })
      ]);
      
      const p = platformsRes.data.find(pl => pl._id === platformId);
      if (!p) {
        navigate(user?.role === 'admin' ? '/admin' : '/dashboard');
        return;
      }
      setPlatform(p);
      setGenerations(generationsRes.data.filter(g => g.platform_id === platformId));
    } catch (error) {
      console.error('Error loading data:', error);
      navigate(user?.role === 'admin' ? '/admin' : '/dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Drag and drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      handleImageFile(file);
    }
  }, []);

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      handleImageFile(file);
    }
  };

  const handleImageFile = (file) => {
    setUploadedImage(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
    setMode('image2video');
  };

  const removeImage = () => {
    setUploadedImage(null);
    setImagePreview(null);
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    if (mode === 'image2video' && !imagePreview) {
      alert('Please upload an image for Image to Video mode');
      return;
    }

    const modelCfg = MODELS[model];
    setGenerating(true);
    try {
      const response = await axios.post(`${API_URL}/api/generations`, {
        platform_id: platformId,
        prompt,
        negative_prompt: negativePrompt,
        generation_type: mode,
        model: modelCfg.model_name,
        mode: modelCfg.mode,
        duration,
        aspect_ratio: aspectRatio,
        cfg_scale: cfgScale,
        image_url: mode === 'image2video' ? imagePreview : null,
      }, { withCredentials: true });

      setGenerations([response.data, ...generations]);
      setSelectedGeneration(response.data);
    } catch (error) {
      alert(error.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  // Poll the selected generation while it's pending/processing
  useEffect(() => {
    if (!selectedGeneration?._id) return;
    if (!['pending', 'processing'].includes(selectedGeneration.status)) return;

    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/generations/${selectedGeneration._id}`,
          { withCredentials: true }
        );
        if (cancelled) return;
        setSelectedGeneration(data);
        setGenerations((prev) => prev.map((g) => (g._id === data._id ? data : g)));
      } catch (e) {
        // swallow — keep polling
      }
    };
    const id = setInterval(tick, 8000);
    return () => { cancelled = true; clearInterval(id); };
  }, [selectedGeneration?._id, selectedGeneration?.status]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#E60000]/30 border-t-[#E60000] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex">
      {/* Left Panel - Generation Thread/History */}
      <aside className="w-72 bg-[#111] border-r border-white/10 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-white/10">
          <button
            onClick={() => navigate(user?.role === 'admin' ? '/admin' : '/dashboard')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4"
          >
            <ArrowLeft size={18} />
            <span className="text-sm">Back to Dashboard</span>
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#E60000] to-[#FF4444] flex items-center justify-center">
              <Film size={20} />
            </div>
            <div>
              <h1 className="font-bold">{platform?.name}</h1>
              <p className="text-xs text-gray-500">AI Video Generation</p>
            </div>
          </div>
        </div>

        {/* Thread/History */}
        <div className="flex-1 overflow-y-auto p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 px-2">Generation Thread</p>
          
          {generations.length === 0 ? (
            <div className="text-center py-8 px-4">
              <Clock size={32} className="mx-auto text-gray-700 mb-2" />
              <p className="text-sm text-gray-500">No generations yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {generations.map((gen) => (
                <motion.button
                  key={gen._id}
                  onClick={() => setSelectedGeneration(gen)}
                  className={`w-full text-left p-3 rounded-xl transition-all ${
                    selectedGeneration?._id === gen._id 
                      ? 'bg-[#E60000]/20 border border-[#E60000]/30' 
                      : 'bg-white/5 border border-transparent hover:bg-white/10'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-start gap-2">
                    <div className={`w-6 h-6 rounded flex-shrink-0 flex items-center justify-center ${
                      gen.generation_type === 'image2video' ? 'bg-purple-500/20' : 'bg-blue-500/20'
                    }`}>
                      {gen.generation_type === 'image2video' ? <Image size={12} className="text-purple-400" /> : <Video size={12} className="text-blue-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-300 line-clamp-2">{gen.prompt}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                          gen.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                          gen.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {gen.status}
                        </span>
                        <span className="text-[10px] text-gray-600">{gen.duration}s</span>
                      </div>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col">
        {/* Mode Toggle Header */}
        <header className="border-b border-white/10 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 bg-white/5 rounded-xl p-1">
              <button
                onClick={() => setMode('text2video')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                  mode === 'text2video' ? 'bg-[#E60000] text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                <Video size={16} />
                Text to Video
              </button>
              <button
                onClick={() => setMode('image2video')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                  mode === 'image2video' ? 'bg-[#E60000] text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                <Image size={16} />
                Image to Video
              </button>
            </div>

            <button
              onClick={() => setShowSettings(!showSettings)}
              className={`p-2 rounded-lg transition-colors ${showSettings ? 'bg-[#E60000] text-white' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}
            >
              <Settings size={20} />
            </button>
          </div>
        </header>

        {/* Generation Interface */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-4xl mx-auto">
            {/* Model Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-3">Select Model</label>
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(MODELS).map(([key, m]) => (
                  <button
                    key={key}
                    onClick={() => {
                      setModel(key);
                      if (!m.durations.includes(duration)) setDuration(m.durations[0]);
                    }}
                    className={`relative p-4 rounded-xl border transition-all text-left ${
                      model === key 
                        ? 'bg-[#E60000]/10 border-[#E60000]' 
                        : 'bg-white/5 border-white/10 hover:border-white/30'
                    }`}
                  >
                    {model === key && (
                      <div className="absolute top-2 right-2">
                        <Check size={16} className="text-[#E60000]" />
                      </div>
                    )}
                    <div className="flex items-center gap-2 mb-1">
                      <Layers size={16} className={model === key ? 'text-[#E60000]' : 'text-gray-500'} />
                      <span className="font-medium text-sm">{m.name}</span>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                      m.badge === 'Recommended' ? 'bg-green-500/20 text-green-400' :
                      m.badge === 'High Quality' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {m.badge}
                    </span>
                    <p className="text-[10px] text-gray-500 mt-2">{m.resolutions[0]} • {m.durations.join('/')}s</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Image Upload (for image2video mode) */}
            <AnimatePresence>
              {mode === 'image2video' && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-6"
                >
                  <label className="block text-sm font-medium text-gray-300 mb-3">Upload Image</label>
                  
                  {!imagePreview ? (
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={`relative border-2 border-dashed rounded-2xl p-8 text-center transition-all ${
                        isDragging 
                          ? 'border-[#E60000] bg-[#E60000]/10' 
                          : 'border-white/20 hover:border-white/40 bg-white/5'
                      }`}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileInput}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      <Upload size={40} className={`mx-auto mb-3 ${isDragging ? 'text-[#E60000]' : 'text-gray-500'}`} />
                      <p className="text-gray-400 mb-1">Drag and drop your image here</p>
                      <p className="text-sm text-gray-600">or click to browse</p>
                      <p className="text-xs text-gray-600 mt-3">Supports: JPG, PNG, WebP (max 10MB)</p>
                    </div>
                  ) : (
                    <div className="relative rounded-2xl overflow-hidden bg-white/5">
                      <img 
                        src={imagePreview} 
                        alt="Uploaded" 
                        className="w-full max-h-64 object-contain"
                      />
                      <button
                        onClick={removeImage}
                        className="absolute top-3 right-3 p-2 bg-black/70 rounded-lg hover:bg-[#E60000] transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Prompt Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-3">
                <Wand2 size={14} className="inline mr-2" />
                Prompt
              </label>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={mode === 'image2video' 
                  ? "Describe the motion and animation you want for your image..." 
                  : "Describe the video you want to generate in detail..."}
                rows={4}
                className="w-full px-4 py-3 bg-[#111] border border-white/10 rounded-xl text-white placeholder-gray-500 resize-none focus:border-[#E60000] focus:ring-1 focus:ring-[#E60000] text-sm"
              />
              <p className="text-xs text-gray-600 mt-2">
                Tip: Be specific about camera movements, lighting, and scene details for better results.
              </p>
            </div>

            {/* Advanced Settings */}
            <AnimatePresence>
              {showSettings && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-6 p-5 bg-white/5 rounded-xl border border-white/10"
                >
                  <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
                    <Settings size={14} />
                    Advanced Settings
                  </h3>
                  
                  {/* Negative Prompt */}
                  <div className="mb-4">
                    <label className="block text-xs font-medium text-gray-400 mb-2">Negative Prompt</label>
                    <Textarea
                      value={negativePrompt}
                      onChange={(e) => setNegativePrompt(e.target.value)}
                      placeholder="Elements to avoid in generation (e.g., blur, distortion, watermark)..."
                      rows={2}
                      className="w-full px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-lg text-white placeholder-gray-600 resize-none text-xs"
                    />
                  </div>

                  {/* CFG Scale */}
                  <div className="mb-4">
                    <label className="block text-xs font-medium text-gray-400 mb-2">
                      CFG Scale: {cfgScale.toFixed(2)}
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={cfgScale}
                      onChange={(e) => setCfgScale(parseFloat(e.target.value))}
                      className="w-full accent-[#E60000]"
                    />
                    <p className="text-[10px] text-gray-600 mt-1">Lower values = more creative, Higher values = more prompt adherence</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Duration & Aspect Ratio */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              {/* Duration */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">Duration</label>
                <div className="flex gap-2">
                  {MODELS[model].durations.map((d) => (
                    <button
                      key={d}
                      onClick={() => setDuration(d)}
                      className={`flex-1 py-3 rounded-xl font-medium text-sm transition-all ${
                        duration === d 
                          ? 'bg-[#E60000] text-white' 
                          : 'bg-white/5 text-gray-400 hover:bg-white/10'
                      }`}
                    >
                      {d}s
                    </button>
                  ))}
                </div>
              </div>

              {/* Aspect Ratio */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">Aspect Ratio</label>
                <div className="flex gap-2">
                  {ASPECT_RATIOS.map((ar) => (
                    <button
                      key={ar.value}
                      onClick={() => setAspectRatio(ar.value)}
                      className={`flex-1 py-3 rounded-xl font-medium text-sm transition-all flex flex-col items-center ${
                        aspectRatio === ar.value 
                          ? 'bg-[#E60000] text-white' 
                          : 'bg-white/5 text-gray-400 hover:bg-white/10'
                      }`}
                    >
                      <span className="text-lg mb-0.5">{ar.icon}</span>
                      <span className="text-[10px]">{ar.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Generate Button */}
            <motion.button
              onClick={handleGenerate}
              disabled={generating || !prompt.trim() || (mode === 'image2video' && !imagePreview)}
              className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-[#E60000] to-[#FF4444] text-white py-4 rounded-xl font-bold text-lg hover:from-[#CC0000] hover:to-[#E60000] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#E60000]/20"
              whileHover={{ scale: generating ? 1 : 1.02 }}
              whileTap={{ scale: generating ? 1 : 0.98 }}
            >
              {generating ? (
                <>
                  <RefreshCw size={22} className="animate-spin" />
                  Generating Video...
                </>
              ) : (
                <>
                  <Sparkles size={22} />
                  Generate Video
                </>
              )}
            </motion.button>
          </div>
        </div>
      </main>

      {/* Right Panel - Preview/Result */}
      <aside className="w-80 bg-[#111] border-l border-white/10 flex flex-col">
        <div className="p-4 border-b border-white/10">
          <h2 className="font-bold flex items-center gap-2">
            <Monitor size={16} />
            Preview
          </h2>
        </div>

        <div className="flex-1 p-4 flex flex-col">
          {selectedGeneration ? (
            <div className="flex-1 flex flex-col">
              {/* Preview Area */}
              <div className="aspect-video bg-black/50 rounded-xl mb-4 flex items-center justify-center overflow-hidden">
                {selectedGeneration.status === 'completed' && selectedGeneration.result_url ? (
                  <div className="relative w-full h-full">
                    <video
                      src={selectedGeneration.result_url}
                      controls
                      className="w-full h-full object-contain"
                    />
                  </div>
                ) : selectedGeneration.status === 'pending' ? (
                  <div className="text-center">
                    <RefreshCw size={32} className="mx-auto text-[#E60000] animate-spin mb-2" />
                    <p className="text-sm text-gray-400">Processing...</p>
                  </div>
                ) : (
                  <div className="text-center">
                    <Play size={40} className="mx-auto text-gray-600 mb-2" />
                    <p className="text-sm text-gray-500">Video preview</p>
                  </div>
                )}
              </div>

              {/* Generation Details */}
              <div className="bg-white/5 rounded-xl p-4 space-y-3">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Prompt</p>
                  <p className="text-sm text-gray-300 line-clamp-3">{selectedGeneration.prompt}</p>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-white/5 rounded-lg p-2">
                    <p className="text-[10px] text-gray-500">Type</p>
                    <p className="text-xs font-medium">{selectedGeneration.generation_type === 'image2video' ? 'I2V' : 'T2V'}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-2">
                    <p className="text-[10px] text-gray-500">Duration</p>
                    <p className="text-xs font-medium">{selectedGeneration.duration}s</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-2">
                    <p className="text-[10px] text-gray-500">Ratio</p>
                    <p className="text-xs font-medium">{selectedGeneration.aspect_ratio}</p>
                  </div>
                </div>
                <p className="text-[10px] text-gray-600">
                  {new Date(selectedGeneration.created_at).toLocaleString()}
                </p>
              </div>

              {/* Actions */}
              {selectedGeneration.status === 'completed' && selectedGeneration.result_url && (
                <div className="mt-4 flex gap-2">
                  <a
                    href={selectedGeneration.result_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 bg-[#E60000] text-white py-3 rounded-xl font-medium hover:bg-[#CC0000] transition-colors"
                  >
                    <Download size={16} />
                    Download
                  </a>
                </div>
              )}
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Film size={40} className="mx-auto text-gray-700 mb-3" />
                <p className="text-sm text-gray-500">Select a generation to preview</p>
                <p className="text-xs text-gray-600 mt-1">or create a new one</p>
              </div>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
};

export default KlingToolPage;
